"use client";

import { useGlobal } from "@/app/providers/use-global";
import { Button } from "@/components/ui/button";
import GroqLink from "@/components/ui/groq-link";
import Icon from "@/components/ui/icon";
import { Tooltip } from "@/components/ui/tooltip";
import { useGlobalStats } from "@/hooks/use-global-stats";
import { cn } from "@/lib/utils";
import type { Model } from "@/shared/data/models";
import { useUser } from "@/shared/providers/user-provider";
import { memo, useMemo } from "react";
import UserMenu from "./user-menu";

export default function Footer() {
  const {
    currentModel,
    setIsLoginDialogOpen,
    isFullScreenIframe,
    isUserLoggedIn,
  } = useGlobal();
  const { isStytchInitialized } = useUser();
  const globalStatsData = useGlobalStats();
  const isIframe = useMemo(() => {
    if (globalThis.window) {
      return window.self !== window.top;
    }
    return false;
  }, []);

  const totalRequestsCounter = useMemo(() => {
    return globalStatsData?.data?.totalRequestsCounter ?? null;
  }, [globalStatsData]);

  return (
    <>
      <div className="hidden px-6 py-4 md:group-[.iframe]:grid md:grid-cols-3">
        <div className="flex justify-start items-center">
          <div className={cn("hidden", isFullScreenIframe && "block")}>
            {isStytchInitialized && !isUserLoggedIn ? (
              <Button
                className="justify-start p-0 hover:text-primaryaccent hover:no-underline"
                variant="link"
                onClick={() => setIsLoginDialogOpen(true)}
              >
                <Icon icon="sign-in" className="shrink-0 mr-2 size-5" />
                Sign in to Groq
              </Button>
            ) : (
              <UserMenu triggerClassName="text-sm" />
            )}
          </div>
        </div>
        <div className="flex justify-center items-center">
          <Copy
            currentModel={currentModel}
            totalRequestsCounter={totalRequestsCounter}
          />
        </div>
        <div className="flex justify-end items-center" />
      </div>

      <footer
        className={cn(
          isIframe && !isFullScreenIframe && "invisible",
          "hidden md:grid divide-y divide-border dark:divide-mutedaccent pt-3 group-[.iframe]:hidden",
        )}
      >
        <div className="grid grid-cols-3 items-end pb-2">
          <div className="flex flex-col items-start gap-1 px-6">
            {isStytchInitialized && !isUserLoggedIn ? (
              <Button
                className="justify-start p-0 hover:text-primaryaccent hover:no-underline"
                variant="link"
                onClick={() => setIsLoginDialogOpen(true)}
              >
                <Icon icon="sign-in" className="shrink-0 mr-2 size-5" />
                Sign in to Groq
              </Button>
            ) : (
              <UserMenu triggerClassName="text-sm mb-1" />
            )}
          </div>

          <Copy
            currentModel={currentModel}
            totalRequestsCounter={totalRequestsCounter}
          />

          <div />
        </div>

        <div className="grid grid-cols-3 gap-2 items-center h-16 px-6">
          {/* Socials */}
          <div className="flex justify-start items-center gap-4">
            <GroqLink
              href={{ pathname: process.env.NEXT_PUBLIC_DISCORD }}
              isExternal
            >
              <Icon
                className="fill-none stroke-muted-foreground hover:stroke-primaryaccent dark:stroke-white dark:hover:stroke-primaryaccent"
                icon="discord"
              />
            </GroqLink>
            <GroqLink href={{ pathname: process.env.NEXT_PUBLIC_X }} isExternal>
              <Icon
                className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                icon="x"
                size="sm"
              />
            </GroqLink>
            <GroqLink
              href={{ pathname: process.env.NEXT_PUBLIC_YOUTUBE }}
              isExternal
            >
              <Icon
                className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                icon="youtube"
                height={22}
              />
            </GroqLink>
            <GroqLink
              href={{ pathname: process.env.NEXT_PUBLIC_LINKEDIN }}
              isExternal
            >
              <Icon
                className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                icon="linked-in"
                size="sm"
              />
            </GroqLink>
            <GroqLink
              href={{ pathname: process.env.NEXT_PUBLIC_INSTAGRAM }}
              isExternal
            >
              <Icon
                className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                icon="instagram"
                size="sm"
              />
            </GroqLink>
          </div>

          {/* Mktg Links */}
          <div className="flex justify-center items-center gap-8 whitespace-nowrap">
            <GroqLink
              className="text-content-emphasis font-medium"
              href={{ pathname: process.env.NEXT_PUBLIC_WHY_GROQ }}
              isExternal
              variant="secondary"
            >
              Why Groq
            </GroqLink>

            <Tooltip
              trigger={
                <GroqLink
                  className="text-content-emphasis font-medium group"
                  href={{ pathname: process.env.NEXT_PUBLIC_GROQ_CLOUD }}
                  isExternal
                  variant="secondary"
                >
                  Start building
                </GroqLink>
              }
              providerProps={{ delayDuration: 0 }}
            >
              Developer Playground
            </Tooltip>

            <Tooltip
              trigger={
                <GroqLink
                  className="text-content-emphasis font-medium group"
                  href={{ pathname: "https://groq.com/careers" }}
                  isExternal
                  variant="secondary"
                >
                  Life at Groq
                </GroqLink>
              }
              providerProps={{ delayDuration: 0 }}
            >
              Groq Careers
            </Tooltip>

            <TotalRequests totalRequestsCounter={totalRequestsCounter} />
          </div>

          <div className="flex justify-end items-center" />
        </div>
      </footer>
    </>
  );
}

type TotalRequestsProps = {
  totalRequestsCounter: number | null;
};

const TotalRequests = memo(({ totalRequestsCounter }: TotalRequestsProps) => {
  if (!totalRequestsCounter) {
    return null;
  }

  return (
    <div className="group flex items-center gap-2 relative">
      <Icon icon="groq-badge" />
      <p className="hidden group-hover:block whitespace-nowrap absolute left-8">
        Total Requests: {totalRequestsCounter.toLocaleString("en-US")}
      </p>
    </div>
  );
});

type CopyProps = {
  currentModel: Model | null | undefined;
  totalRequestsCounter: number | null | undefined;
};

const Copy = memo(({ currentModel, totalRequestsCounter }: CopyProps) => {
  return (
    <div className="flex flex-col justify-center items-center gap-1 p-1">
      <h2 className="flex items-center">
        <div className="hidden group-[.iframe]:flex items-center gap-2">
          <Icon icon="groq-badge" />
          <p className="block whitespace-nowrap">
            Total Requests: {totalRequestsCounter?.toLocaleString("en-US")}
          </p>
        </div>
        <div className="flex group-[.iframe]:hidden">
          <div className="whitespace-nowrap">Powered by the Groq </div>
          <div className="text-primaryaccent font-bold ml-1">LPU</div>
        </div>
      </h2>
      <div className="text-[10px] whitespace-nowrap text-muted-text dark:text-muted-foreground">
        {currentModel?.owned_by ?? "LLM"}-based chatbot. Accuracy, correctness
        or appropriateness cannot be guaranteed.
      </div>
    </div>
  );
});
